import { useMsal } from '@azure/msal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import ls from 'localstorage-slim';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { version } from '../../../../../package.json';
import { backendLogin } from '../../../../api/instant-lease-api';
import {
  B2C_BRAND_SESSION_KEY,
  B2C_RECOVERY_ATTEMPT_SESSION_KEY,
  loginRequest,
} from '../../../../auth/msal-config';
import { authenticate } from '../../../../redux/auth/auth-slice';
import { setNotification } from '../../../../redux/cache-slice';
import { RootState } from '../../../../redux/types';
import { isAdb2cEnabled } from '../../../../utils/feature-flags';
import { getPartnerText, getText } from '../../../../utils/getter';
import PartnerLogo from '../../../b2b/components/partner-logo/partner-logo';
import loginImage from '../images/login-image.jpeg';
import PasswordCriteriaHint from '../password-criteria-hint';
import { passwordSchema } from '../password-field';

interface LoginFormData {
  email: string;
  password: string;
}

interface RegisterFormData {
  givenName: string;
  familyName: string;
  email: string;
  password: string;
  accept: boolean;
}

const LoginPage: React.FC = () => {
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { carData } = useSelector((state: RootState) => state.leaseApp);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [formType, setFormType] = useState<'LOGIN' | 'REGISTER'>('LOGIN');

  const adb2cEnabled = isAdb2cEnabled();

  const handleLogin = () => {
    const brand =
      new URLSearchParams(document.location.search).get('brand') ||
      sessionStorage.getItem(B2C_BRAND_SESSION_KEY);
    sessionStorage.removeItem(B2C_RECOVERY_ATTEMPT_SESSION_KEY);
    if (brand) {
      sessionStorage.setItem(B2C_BRAND_SESSION_KEY, brand);
    } else {
      sessionStorage.removeItem(B2C_BRAND_SESSION_KEY);
    }

    instance.loginRedirect({
      ...loginRequest,
      extraQueryParameters: {
        ...loginRequest.extraQueryParameters,
        ...(brand && { brand }),
      },
    });
  };

  // Auth0 email/password login (used when adb2cEnabled is false)
  const auth0LoginSchema = useMemo(
    () =>
      yup.object({
        email: yup
          .string()
          .email(getText('validation_address'))
          .required('Pflichtfeld'),
        password: yup
          .string()
          .required('Pflichtfeld')
          .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/, {
            excludeEmptyString: true,
          }),
      }),
    [],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    mode: 'onTouched',
    resolver: yupResolver(auth0LoginSchema),
  });

  const loginWithCredentials = async (email: string, password: string) => {
    setLoading(true);
    try {
      const loginResponse = await backendLogin(email, password);
      const tokenPayload = JSON.parse(
        atob(loginResponse.access_token.split('.')[1]),
      );
      const authId = tokenPayload.sub ?? '';
      const expiresAt = loginResponse.expires_in * 1000 + Date.now();
      dispatch(
        authenticate({
          accessToken: loginResponse.access_token,
          expiresAt,
          authId,
        }),
      );
      setLoading(false);
    } catch {
      setLoading(false);
      dispatch(
        setNotification({
          notificationVisibility: true,
          notificationHasError: true,
          notificationTitle: 'Es ist ein Fehler aufgetreten.',
          notificationBody:
            'Benutzername oder Passwort falsch, überprüfe deine Zugangsdaten oder versuche es später noch einmal. ',
        }),
      );
    }
  };

  const onSubmit = (data: LoginFormData) => {
    loginWithCredentials(data.email, data.password);
  };

  const registerValidationSchema = useMemo(
    () =>
      yup.object({
        givenName: yup.string().required('Pflichtfeld'),
        familyName: yup.string().required('Pflichtfeld'),
        email: yup
          .string()
          .email(getText('validation_address'))
          .required(getText('validation_address')),
        password: passwordSchema,
        accept: yup
          .boolean()
          .required('Pflichtfeld')
          .oneOf([true], 'Pflichtfeld'),
      }),
    [],
  );

  const {
    register: registerField,
    handleSubmit: handleRegisterSubmit,
    watch: watchRegister,
    formState: { errors: registerErrors },
  } = useForm<RegisterFormData>({
    mode: 'onTouched',
    resolver: yupResolver(registerValidationSchema),
    reValidateMode: 'onChange',
  });

  const registerPassword = watchRegister('password') ?? '';

  const passwordRegister = registerField('password');

  // To test registering/sign up you need to have: offerId, publicationId, and pricingId
  // You can paste this mock data to populate your local storage:
  // http://localhost:3000/b2cOffer?partner=ald_marktplatz&offerId=120851695&publicationId=PUB123&pricingId=PRC456&type=B2C

  const registerUser = async (data: RegisterFormData) => {
    setLoading(true);
    const { givenName, familyName, email, password } = data;
    const offer = ls.get<{
      offerId: string;
      offerRefId?: string;
      publicationId?: string;
      pricingId?: string;
      partner: string;
      loyaltyId?: string;
      loyaltyType?: string;
      type: string;
    }>('oid');
    if (offer) {
      const {
        offerId,
        offerRefId,
        publicationId,
        pricingId,
        partner,
        loyaltyId,
        loyaltyType,
        type,
      } = offer;
      const effectiveOfferId =
        offerId && offerId !== 'null' && offerId !== 'undefined'
          ? offerId
          : undefined;
      const effectiveOfferRefId =
        offerRefId && offerRefId !== 'null' && offerRefId !== 'undefined'
          ? offerRefId
          : undefined;
      try {
        await axios.post('/api/lease-application/signup', {
          email,
          password,
          username: `${givenName}-${familyName}`,
          firstName: givenName,
          lastName: familyName,
          offerId: effectiveOfferId,
          offerRefId: effectiveOfferRefId,
          publicationId: publicationId,
          pricingId: pricingId,
          partner: partner.toUpperCase(),
          loyaltyId,
          loyaltyType,
          type,
        });

        const loginResponse = await backendLogin(email, password);
        const tokenPayload = JSON.parse(
          atob(loginResponse.access_token.split('.')[1]),
        );
        const authId = tokenPayload.sub ?? '';
        const expiresAt = loginResponse.expires_in * 1000 + Date.now();
        dispatch(
          authenticate({
            accessToken: loginResponse.access_token,
            expiresAt,
            authId,
          }),
        );
        setLoading(false);
      } catch {
        setLoading(false);
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: true,
            notificationTitle: 'Es ist ein Fehler aufgetreten.',
            notificationBody: getText('error_happened'),
          }),
        );
      }
    } else {
      setLoading(false);
      dispatch(
        setNotification({
          notificationVisibility: true,
          notificationHasError: true,
          notificationTitle: 'Es ist ein Fehler aufgetreten.',
          notificationBody: getText('error_happened'),
        }),
      );
    }
  };

  return (
    <div className='login-page relative flex min-h-screen flex-col bg-white md:bg-transparent'>
      {/* Header */}
      <header className='relative z-10 flex! h-16 items-center justify-start! bg-white px-6! md:px-8! md:shadow-sm'>
        <PartnerLogo className='mt-0! ml-0! w-auto! md:ml-[10%]!' />
      </header>

      {/* Main content with background image */}
      <div className='relative flex flex-1 items-start justify-center md:items-center'>
        {/* Background image - hidden on mobile */}
        <img
          src={loginImage}
          alt=''
          className='absolute inset-0 hidden! h-full w-full object-cover md:block!'
        />
        {/* Overlay - hidden on mobile */}
        <div className='absolute inset-0 hidden! bg-(--vel-color-secondary-400) opacity-50 md:block!' />

        {/* Card on desktop, plain content on mobile */}
        <div className='relative z-10 my-12! w-full px-6! py-8! md:max-w-md md:min-w-130 md:rounded-3xl md:bg-white md:p-12! md:shadow-2xl'>
          {formType === 'LOGIN' ? (
            <>
              <h1 className='mb-12! text-[33px] leading-tight font-bold text-(--vel-color-secondary-400)!'>
                Melde dich an, um deinen Antrag fortzusetzen
              </h1>

              {adb2cEnabled ? (
                <button
                  type='button'
                  onClick={handleLogin}
                  className='w-full rounded-lg bg-(--vel-color-secondary-500) py-3.5 text-base! font-bold text-white transition-colors hover:bg-(--vel-color-secondary-400)'
                >
                  Anmelden
                </button>
              ) : (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  key='login-form'
                  noValidate
                >
                  {/* Email */}
                  <div className='mb-6!'>
                    <label className='mb-2! block text-sm font-bold text-(--vel-color-secondary-400)!'>
                      E-Mail
                    </label>
                    <input
                      type='email'
                      placeholder='E-Mail eingeben'
                      className={`w-full rounded-md border px-4! py-3! text-sm! transition-colors outline-none ${
                        errors.email
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:border-(--vel-color-primary-400)'
                      }`}
                      {...register('email')}
                    />
                    {errors.email && (
                      <p className='mt-1! text-xs text-red-500'>
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  {/* Password */}
                  <div className='mb-6!'>
                    <label className='mb-2! block text-sm font-bold text-(--vel-color-secondary-400)!'>
                      Passwort
                    </label>
                    <div className='relative'>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={`w-full rounded-md border px-4! py-3! pr-20! text-sm! transition-colors outline-none ${
                          errors.password
                            ? 'border-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:border-(--vel-color-primary-400)'
                        }`}
                        {...register('password')}
                      />
                      <button
                        type='button'
                        onClick={() => setShowPassword(!showPassword)}
                        className='absolute top-1/2 right-3 -translate-y-1/2 text-xs! text-gray-500 hover:text-gray-700'
                      >
                        {showPassword ? 'Verbergen' : 'Anzeigen'}
                      </button>
                    </div>
                    {errors.password && (
                      <p className='mt-1! text-xs text-red-500'>
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  {/* Forgot Password */}
                  <div className='mb-2!'>
                    <NavLink
                      to='/reset-password'
                      className='text-sm! font-bold text-(--vel-color-secondary-400)!'
                    >
                      {getText('forget_password')}
                    </NavLink>
                  </div>

                  {/* Submit Button */}
                  <button
                    type='submit'
                    disabled={loading}
                    className='w-full rounded-lg bg-(--vel-color-secondary-500) py-3.5 text-base! font-bold text-white transition-colors hover:bg-(--vel-color-secondary-400) disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    {loading ? 'Wird geladen...' : 'Anmelden'}
                  </button>
                </form>
              )}

              {/* Register link */}
              {carData && (
                <>
                  <hr className='mt-12! mb-6! border-(--vel-color-neutral-100)!' />
                  <div>
                    <p className='mb-3! font-bold! text-(--vel-color-neutral-500)!'>
                      Noch keinen Account?
                    </p>
                    <button
                      type='button'
                      onClick={() => {
                        if (adb2cEnabled) {
                          navigate('/register');
                        } else {
                          setFormType('REGISTER');
                        }
                      }}
                      className='cursor-pointer border-none bg-transparent p-0! text-sm! font-bold! text-(--vel-color-primary-400)!'
                    >
                      Jetzt registrieren
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <h1 className='mb-12! text-[33px] leading-tight font-bold text-(--vel-color-secondary-400)!'>
                Erstelle deinen Account
              </h1>

              <form
                key='register-form'
                onSubmit={handleRegisterSubmit((data) => registerUser(data))}
                noValidate
              >
                {/* First Name */}
                <div className='mb-6!'>
                  <label className='mb-2! block text-sm font-bold text-(--vel-color-secondary-400)!'>
                    Vorname(n)
                  </label>
                  <input
                    type='text'
                    className={`w-full rounded-md border px-4! py-3! text-sm! transition-colors outline-none ${
                      registerErrors.givenName
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:border-(--vel-color-primary-400)'
                    }`}
                    {...registerField('givenName')}
                  />
                  {registerErrors.givenName && (
                    <p className='mt-1! text-xs text-red-500'>
                      {registerErrors.givenName.message}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div className='mb-6!'>
                  <label className='mb-2! block text-sm font-bold text-(--vel-color-secondary-400)!'>
                    Nachname(n)
                  </label>
                  <input
                    type='text'
                    className={`w-full rounded-md border px-4! py-3! text-sm! transition-colors outline-none ${
                      registerErrors.familyName
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:border-(--vel-color-primary-400)'
                    }`}
                    {...registerField('familyName')}
                  />
                  {registerErrors.familyName && (
                    <p className='mt-1! text-xs text-red-500'>
                      {registerErrors.familyName.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className='mb-6!'>
                  <label className='mb-2! block text-sm font-bold text-(--vel-color-secondary-400)!'>
                    E-Mail-Adresse
                  </label>
                  <input
                    type='email'
                    className={`w-full rounded-md border px-4! py-3! text-sm! transition-colors outline-none ${
                      registerErrors.email
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:border-(--vel-color-primary-400)'
                    }`}
                    {...registerField('email')}
                  />
                  {registerErrors.email && (
                    <p className='mt-1! text-xs text-red-500'>
                      {registerErrors.email.message}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div className='mb-6!'>
                  <label className='mb-2! block text-sm font-bold text-(--vel-color-secondary-400)!'>
                    Passwort
                  </label>
                  <div className='relative'>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`w-full rounded-md border px-4! py-3! pr-20! text-sm! transition-colors outline-none ${
                        registerErrors.password
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:border-(--vel-color-primary-400)'
                      }`}
                      {...passwordRegister}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={(e) => {
                        setPasswordFocused(false);
                        passwordRegister.onBlur(e);
                      }}
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute top-1/2 right-3 -translate-y-1/2 text-xs! text-gray-500 hover:text-gray-700'
                    >
                      {showPassword ? 'Verbergen' : 'Anzeigen'}
                    </button>
                  </div>
                  <PasswordCriteriaHint
                    password={registerPassword}
                    focused={passwordFocused}
                  />
                </div>

                {/* Accept Checkbox */}
                <div className='mb-6!'>
                  <label className='flex! cursor-pointer items-start gap-3!'>
                    <input
                      type='checkbox'
                      className='mt-1! h-4! w-4! accent-(--vel-color-primary-400)'
                      {...registerField('accept')}
                    />
                    <span className='text-sm! text-(--vel-color-secondary-400)!'>
                      Ich habe die{' '}
                      <a
                        href={`${getPartnerText('datenschutzhinweise_link')}`}
                        target='_blank'
                        rel='noreferrer'
                        className='font-bold text-(--vel-color-primary-400)!'
                      >
                        Datenschutzhinweise
                      </a>{' '}
                      zur Kenntnis genommen
                    </span>
                  </label>
                  {registerErrors.accept && (
                    <p className='mt-1! text-xs text-red-500'>
                      {registerErrors.accept.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type='submit'
                  disabled={loading}
                  className='w-full rounded-lg bg-(--vel-color-secondary-500) py-3.5 text-base! font-bold text-white transition-colors hover:bg-(--vel-color-secondary-400) disabled:cursor-not-allowed disabled:opacity-50'
                >
                  {loading ? 'Wird geladen...' : 'Jetzt registrieren'}
                </button>
              </form>

              {/* Back to login link */}
              <hr className='mt-12! mb-6! border-(--vel-color-neutral-100)!' />
              <div>
                <p className='mb-3! font-bold! text-(--vel-color-neutral-500)!'>
                  Bereits einen Account?
                </p>
                <button
                  type='button'
                  onClick={() => setFormType('LOGIN')}
                  className='cursor-pointer border-none bg-transparent p-0! text-sm! font-bold! text-(--vel-color-primary-400)!'
                >
                  Jetzt anmelden
                </button>
              </div>
            </>
          )}

          {/* Marketplace link */}
          <hr className='mt-12! mb-4! border-gray-200' />
          <div>
            <span className='text-sm text-(--vel-color-secondary-400)!'>
              Noch kein Fahrzeug?{' '}
            </span>
            <a
              href={import.meta.env.VITE_MOPO_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='text-sm font-bold text-(--vel-color-primary-400)!'
            >
              Fahrzeug auswählen
            </a>
          </div>
        </div>
      </div>
      <footer className='bg-(--footer-background) px-6! py-3! text-right text-xs! text-(--footer-text)!'>
        Version {version}
      </footer>
    </div>
  );
};

export default LoginPage;
